import React, { useCallback, useEffect, useMemo } from 'react'

import Image from 'next/image'
import { Control, useForm, UseFormSetValue, useWatch } from 'react-hook-form'

import { SelectInput } from '~/components/component-library'
import { StepButtons } from '~/components/create-workspace/StepButtons'
import { useSetTreasuryPasskey } from '~/components/create-workspace/useSetTreasuryPasskey'
import {
  useActiveOrgSmartAccountsSetup,
  useRequiredActiveOrg,
} from '~/hooks/org'
import { useRequiredAuthUser } from '~/hooks/useAuthUser'
import { useServerRequests } from '~/hooks/useRequests'
import { OrgMemberRole, OrgPasskey } from '~/types'
import { isDarkMode } from '~/utils'

type FormValues = {
  treasuryThreshold: number
  treasuryPasskeyId: string
}

export function TreasuryStep(props: {
  stepForward: () => void
  stepBack: () => void
}) {
  const user = useRequiredAuthUser()
  const org = useRequiredActiveOrg()
  const setup = useActiveOrgSmartAccountsSetup()
  const owners = org.members.filter((m) => m.role === OrgMemberRole.Owner)

  const defaultTreasuryThreshold =
    setup?.thresholds?.find((t) => t.setupAccount === 'TREASURY')?.threshold ??
    1

  const ownerCount = owners.length
  const invitationCount = org.invitations.length
  const totalCount = ownerCount + invitationCount

  const userPasskeys = useMemo(
    () => org.passkeys.filter((p) => p.user?.id === user.id) as OrgPasskey[],
    [org.passkeys, user.id],
  )

  const setupPasskeys = useMemo(
    () => setup?.authenticators?.filter((a) => a.setupAccount === 'TREASURY'),
    [setup?.authenticators],
  )

  const currentTreasuryPasskeyId = userPasskeys.find((p) =>
    setupPasskeys?.find((sp) => sp.authenticatorId === p.id),
  )?.id

  return (
    <TreasuryStepInner
      orgId={org.id}
      defaultTreasuryThreshold={defaultTreasuryThreshold}
      maxPossibleThreshold={totalCount}
      recommendedThreshold={getRecommendedTreasuryThreshold(totalCount)}
      userPasskeys={userPasskeys}
      currentTreasuryPasskeyId={currentTreasuryPasskeyId}
      {...props}
    />
  )
}

const TreasuryStepInner = React.memo(
  ({
    orgId,
    defaultTreasuryThreshold,
    maxPossibleThreshold,
    recommendedThreshold,
    userPasskeys,
    currentTreasuryPasskeyId,
    stepForward,
    stepBack,
  }: {
    orgId: string
    defaultTreasuryThreshold: number
    maxPossibleThreshold: number
    recommendedThreshold: number
    userPasskeys: OrgPasskey[]
    currentTreasuryPasskeyId: string | undefined
    stepForward: () => void
    stepBack: () => void
  }) => {
    const { serverPostRequest, isProcessing: isProcessingUpdate } =
      useServerRequests()
    const { setTreasuryPasskey, isSettingTreasuryPasskey } =
      useSetTreasuryPasskey()

    const { control, setValue } = useForm<FormValues>({
      defaultValues: {
        treasuryThreshold: defaultTreasuryThreshold,
        treasuryPasskeyId: currentTreasuryPasskeyId ?? '',
      },
    })
    const threshold = useWatch({ control, name: 'treasuryThreshold' })
    const treasuryPasskeyId = useWatch({ control, name: 'treasuryPasskeyId' })

    const setTreasuryThreshold = useCallback(
      async (threshold: number) => {
        /** Update threshold in database */
        await serverPostRequest({
          path: '/api/org/smart_accounts_setup/set_treasury_threshold',
          params: { orgId, threshold },
          errorMessage: 'Failed to update Treasury approvals',
        }).then(() => {
          /** Update form state */
          setValue('treasuryThreshold', threshold)
        })
      },
      [setValue, orgId, serverPostRequest],
    )

    const handleStepForward = useCallback(async () => {
      // If user has exactly 1 passkey and no treasury passkey set, set it automatically
      if (userPasskeys.length === 1 && !currentTreasuryPasskeyId) {
        const result = await setTreasuryPasskey(userPasskeys[0].id)
        if (!result) return
      }
      stepForward()
    }, [
      userPasskeys,
      currentTreasuryPasskeyId,
      setTreasuryPasskey,
      stepForward,
    ])

    useEffect(() => {
      // Update form value when the threshold changes
      setValue('treasuryThreshold', defaultTreasuryThreshold)
    }, [defaultTreasuryThreshold, setValue])

    useEffect(() => {
      // Update form value when the passkey changes
      if (currentTreasuryPasskeyId) {
        setValue('treasuryPasskeyId', currentTreasuryPasskeyId)
      }
    }, [currentTreasuryPasskeyId, setValue])

    return (
      <>
        <TreasuryPasskeySelector
          control={control}
          setValue={setValue}
          currentTreasuryPasskeyId={currentTreasuryPasskeyId}
          userPasskeys={userPasskeys}
        />
        <SelectInput
          label="Approvals required"
          footer="The number of owners that must approve each Treasury transaction"
          control={control}
          inputName="treasuryThreshold"
          options={Array.from({ length: maxPossibleThreshold }).map((_, i) => ({
            value: i + 1,
            label: `${i + 1} of ${maxPossibleThreshold} owner${
              maxPossibleThreshold === 1 ? '' : 's'
            } ${i + 1 === recommendedThreshold ? ' (recommended)' : ''}`,
          }))}
          selectValue={setTreasuryThreshold}
          placeholder="Select an option"
          selectedOption={threshold}
        />
        <StepButtons
          isDisabled={
            isProcessingUpdate ||
            isSettingTreasuryPasskey ||
            (userPasskeys.length >= 2 && !treasuryPasskeyId)
          }
          isLoading={isSettingTreasuryPasskey}
          onNextClick={handleStepForward}
          onBackClick={stepBack}
        />
      </>
    )
  },
)

TreasuryStepInner.displayName = 'TreasuryStep'

function TreasuryPasskeySelector({
  control,
  setValue,
  currentTreasuryPasskeyId,
  userPasskeys,
}: {
  control: Control<FormValues>
  setValue: UseFormSetValue<FormValues>
  currentTreasuryPasskeyId: string | undefined
  userPasskeys: OrgPasskey[]
}) {
  const { setTreasuryPasskey } = useSetTreasuryPasskey()
  const treasuryPasskeyId = useWatch({ control, name: 'treasuryPasskeyId' })

  const onSetTreasuryPasskey = useCallback(
    (passkeyId: string) =>
      setTreasuryPasskey(passkeyId, () => {
        setValue('treasuryPasskeyId', currentTreasuryPasskeyId ?? '')
      }),
    [setTreasuryPasskey, currentTreasuryPasskeyId, setValue],
  )

  if (userPasskeys.length < 2) {
    return null
  }

  return (
    <SelectInput
      label="Signer for Treasury"
      control={control}
      inputName="treasuryPasskeyId"
      footer="What you will use to approve transfers and other Treasury actions"
      options={userPasskeys.map((p) => {
        const iconSrc = isDarkMode() ? p.providerIconDark : p.providerIconLight

        return {
          value: p.id,
          icon: iconSrc ? (
            <Image
              src={iconSrc}
              alt={`Icon for ${p.name}`}
              width={20}
              height={20}
            />
          ) : (
            <div className="h-5 w-5 rounded-full bg-gray-200 dark:bg-gray-700" />
          ),
          label: p.name,
        }
      })}
      placeholder="Select an option"
      selectValue={onSetTreasuryPasskey}
      selectedOption={treasuryPasskeyId ?? ''}
    />
  )
}

export function getRecommendedTreasuryThreshold(ownerLen: number): number {
  // factor in the signed in user as 1
  if (ownerLen === 1) return 1
  if (ownerLen === 2) return 2
  if (ownerLen === 3) return 2
  return Math.floor(ownerLen / 2) + 1
}
